import React, { useEffect, useRef, useState, useMemo } from 'react';
import { supabase } from 'lib/supabaseClient';
import { EditorialLogo } from 'components/MenuBar/GlobalHeader';

/**
 * LandingPageTrimmed - Focused version for early-stage traction
 *
 * Sections kept:
 * - Navigation (simplified)
 * - Hero (tightened messaging around the loop)
 * - How It Works (the core loop: Identify → Collaborate → Act)
 * - Key Differentiators (2-3, not 5)
 * - Single CTA (join / sign up)
 * - Footer (simplified)
 *
 * Sections removed:
 * - Problem/Solution (merged into hero)
 * - For Changemakers (5 principles — too vision-level for early users)
 * - Capabilities (5 features — oversells current state)
 * - Timeline (better for About page)
 *
 * TEMPORARY: accessible at /landing-preview for comparison
 */

// ============================================
// SHARED STYLES
// ============================================
const SharedStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Source+Sans+3:wght@400;500;600&display=swap');

    .landing-redesign {
      --color-cream: #FAF7F2;
      --color-warm-white: #FFFEF9;
      --color-charcoal: #1C1917;
      --color-charcoal-soft: #44403C;
      --color-charcoal-muted: #78716C;
      --color-terracotta: #C2410C;
      --color-terracotta-light: #EA580C;
      --color-olive: #4D7C0F;
      --color-olive-light: #65A30D;
      --color-ochre: #A16207;
      --color-sand: #E7E5E4;
      --color-sand-dark: #D6D3D1;

      font-family: 'Source Sans 3', -apple-system, sans-serif;
      background: var(--color-cream);
      color: var(--color-charcoal);
    }

    .font-editorial {
      font-family: 'Fraunces', Georgia, serif;
    }

    .paper-texture::before {
      content: '';
      position: absolute;
      inset: 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
      opacity: 0.03;
      pointer-events: none;
      mix-blend-mode: multiply;
    }

    .hand-underline {
      position: relative;
      display: inline;
    }
    .hand-underline::after {
      content: '';
      position: absolute;
      left: -4px;
      right: -4px;
      bottom: -2px;
      height: 8px;
      background: url("data:image/svg+xml,%3Csvg width='100' height='8' viewBox='0 0 100 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 5C20 2 30 6 50 4C70 2 80 6 100 3' stroke='%23C2410C' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") repeat-x;
      background-size: 100px 8px;
    }

    @keyframes rise-up {
      from { opacity: 0; transform: translateY(40px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .animate-rise {
      animation: rise-up 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }

    .animate-fade {
      animation: fade-in 0.6s ease-out forwards;
    }

    .delay-1 { animation-delay: 0.1s; }
    .delay-2 { animation-delay: 0.2s; }
    .delay-3 { animation-delay: 0.3s; }
    .delay-4 { animation-delay: 0.4s; }
    .delay-5 { animation-delay: 0.5s; }

    .organic-shape {
      border-radius: 60% 40% 50% 50% / 50% 60% 40% 50%;
    }
  `}</style>
);

// ============================================
// SCROLL ANIMATION HOOK
// ============================================
const useIntersectionObserver = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const options = useMemo(() => ({ threshold, rootMargin: '0px 0px -50px 0px' }), [threshold]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, options);

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options]);

  return { ref, isVisible };
};

// ============================================
// NAVIGATION (simplified — fewer scroll links)
// ============================================
const Navigation: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const exploreLinks = [
    { label: 'Browse Issues', href: '/issues' },
    { label: 'Browse Solutions', href: '/solutions' },
    { label: 'Action Groups', href: '/action-groups' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen ? 'py-3 shadow-sm' : 'py-5'
      }`}
      style={{
        background: scrolled || mobileMenuOpen ? 'rgba(250,247,242,0.98)' : 'transparent',
        backdropFilter: scrolled || mobileMenuOpen ? 'blur(10px)' : 'none'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          <EditorialLogo />

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-sm font-medium transition-colors hover:opacity-70"
              style={{ color: 'var(--color-charcoal-soft)' }}
            >
              How It Works
            </button>

            {/* Explore links */}
            {exploreLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium transition-colors hover:opacity-70"
                style={{ color: 'var(--color-charcoal-soft)' }}
              >
                {link.label}
              </a>
            ))}

            <a
              href="/sign-in"
              className="text-sm font-semibold px-5 py-2.5 rounded-full transition-all hover:scale-105"
              style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}
            >
              Get Started
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t pt-4 space-y-4" style={{ borderColor: 'var(--color-sand)' }}>
            <button onClick={() => scrollToSection('how-it-works')} className="block text-base">How It Works</button>
            {exploreLinks.map((link) => (
              <a key={link.href} href={link.href} className="block text-base" style={{ color: 'var(--color-charcoal-soft)' }}>
                {link.label}
              </a>
            ))}
            <a href="/sign-in" className="inline-block px-5 py-2.5 rounded-full text-sm font-semibold mt-2" style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}>
              Get Started
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// ============================================
// HERO SECTION (tightened — problem/solution merged in)
// ============================================
const HeroSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen pt-24 pb-16 paper-texture overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--color-terracotta)' }} />
      <div className="absolute top-20 right-0 w-64 h-64 organic-shape opacity-[0.04]" style={{ background: 'var(--color-terracotta)' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 min-h-[calc(100vh-6rem)] flex flex-col">
        <div className="flex-1 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center py-12">
          {/* Left Column */}
          <div className="lg:col-span-7">
            <div className={`mb-6 ${isVisible ? 'animate-rise delay-1' : 'opacity-0'}`}>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--color-olive)' }}>
                <span className="w-8 h-px" style={{ background: 'var(--color-olive)' }} />
                Decentralized Philanthropy
              </span>
            </div>

            <h1 className={`font-editorial leading-[0.95] mb-8 ${isVisible ? 'animate-rise delay-2' : 'opacity-0'}`}>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light">
                See a problem.
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light">
                Propose a <span className="italic font-normal" style={{ color: 'var(--color-charcoal-soft)' }}>solution.</span>
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold mt-2">
                <span className="hand-underline" style={{ color: 'var(--color-terracotta)' }}>Take action</span>
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold">
                together.
              </span>
            </h1>

            <p className={`text-lg sm:text-xl max-w-xl leading-relaxed mb-10 ${isVisible ? 'animate-rise delay-3' : 'opacity-0'}`} style={{ color: 'var(--color-charcoal-soft)' }}>
              WEattitude is where anyone can report social issues, propose solutions,
              and form action groups to create real, measurable change. No bureaucracy.
              No gatekeepers. Just people solving problems together.
            </p>

            {/* CTAs */}
            <div className={`flex flex-wrap items-center gap-4 ${isVisible ? 'animate-rise delay-4' : 'opacity-0'}`}>
              <a
                href="/sign-in"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full font-semibold transition-all hover:scale-105 hover:shadow-lg"
                style={{ background: 'var(--color-terracotta)', color: 'var(--color-warm-white)' }}
              >
                Get Started — It's Free
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="/issues"
                className="group inline-flex items-center gap-2 px-6 py-4 rounded-full font-semibold border-2 transition-all hover:scale-105"
                style={{ borderColor: 'var(--color-charcoal)', color: 'var(--color-charcoal)' }}
              >
                Browse Issues
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right Column - Video */}
          <div className={`lg:col-span-5 relative ${isVisible ? 'animate-rise delay-4' : 'opacity-0'}`}>
            <div className="relative">
              <div className="relative aspect-video rounded-sm overflow-hidden shadow-xl" style={{ background: 'var(--color-charcoal)' }}>
                {videoPlaying ? (
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/-oQP9JVDUNg?rel=0&modestbranding=1&autoplay=1"
                    title="WEattitude.org - Decentralized Philanthropy"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <button
                    onClick={() => setVideoPlaying(true)}
                    className="absolute inset-0 w-full h-full group cursor-pointer"
                    aria-label="Play video"
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        background: 'linear-gradient(135deg, var(--color-charcoal) 0%, #2D2926 50%, var(--color-charcoal) 100%)',
                      }}
                    />
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-4 left-4 w-24 h-24 border-2 border-current rounded-full" style={{ color: 'var(--color-terracotta)' }} />
                      <div className="absolute bottom-8 right-8 w-32 h-32 border border-current" style={{ color: 'var(--color-sand)' }} />
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      <div className="mb-6 text-center">
                        <span className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
                          WE<span className="italic" style={{ fontFamily: "'Fraunces', serif" }}>attitude</span><span className="opacity-50">.org</span>
                        </span>
                      </div>
                      <div
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                        style={{
                          background: 'var(--color-terracotta)',
                          boxShadow: '0 8px 30px rgba(194, 65, 12, 0.4)'
                        }}
                      >
                        <svg className="w-6 h-6 sm:w-8 sm:h-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <p className="mt-4 text-sm font-medium tracking-wide opacity-70">
                        Watch the Demo
                      </p>
                    </div>
                  </button>
                )}
              </div>

              <div className="absolute -top-4 -right-4 max-w-[200px] p-4" style={{ background: 'var(--color-olive)', color: 'var(--color-warm-white)' }}>
                <p className="font-editorial text-sm italic leading-snug">"Change rises from those who live the reality"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom trust bar */}
        <div className={`mt-auto pt-8 border-t ${isVisible ? 'animate-fade delay-5' : 'opacity-0'}`} style={{ borderColor: 'var(--color-sand)' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm" style={{ color: 'var(--color-charcoal-soft)' }}>
              Built by communities, for communities. <span className="font-semibold">Open source</span> & <span className="font-semibold">transparent</span>.
            </p>
            <div className="flex items-center gap-6">
              {['Free Forever', 'Non-Profit', '501(c)(3)'].map((badge) => (
                <span key={badge} className="text-xs font-medium tracking-wide uppercase opacity-60">{badge}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div id="hero-end-trigger" className="absolute bottom-0 left-0 right-0 h-1" aria-hidden="true" />
    </section>
  );
};

// ============================================
// HOW IT WORKS SECTION (kept as-is — this is the core loop)
// ============================================
const HowItWorksSection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();

  const steps = [
    {
      number: '01',
      title: 'Identify',
      subtitle: 'See a problem? Report it.',
      description: 'Spot issues in your community or globally. Add context, location, and details. Your local knowledge matters.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      cta: { label: 'Report Issues', href: '/issues' },
    },
    {
      number: '02',
      title: 'Collaborate',
      subtitle: 'Propose solutions together.',
      description: 'Share ideas, discuss approaches, vote on the best solutions. Collective wisdom beats individual expertise.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      cta: { label: 'Recommend Solutions', href: '/solutions' },
    },
    {
      number: '03',
      title: 'Act',
      subtitle: 'Form groups. Take action.',
      description: 'Join action groups, contribute your skills, and work together to create measurable, lasting change.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      cta: { label: 'Join Action Groups', href: '/action-groups' },
    },
  ];

  return (
    <section id="how-it-works" className="py-24 lg:py-32 paper-texture relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-[0.02]" style={{ background: 'var(--color-terracotta)' }} />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center max-w-2xl mx-auto mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: 'var(--color-terracotta)' }}>
            Simple Process
          </span>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
            How It <span className="hand-underline" style={{ color: 'var(--color-terracotta)' }}>Works</span>
          </h2>
          <p className="text-lg" style={{ color: 'var(--color-charcoal-soft)' }}>
            Three steps to transform your ideas into real-world impact.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="p-8 rounded-sm h-full" style={{ background: 'var(--color-warm-white)', border: '1px solid var(--color-sand)' }}>
                <div className="flex items-start justify-between mb-6">
                  <span className="font-editorial text-4xl font-light" style={{ color: 'var(--color-terracotta)', opacity: 0.4 }}>
                    {step.number}
                  </span>
                  <div style={{ color: 'var(--color-charcoal-soft)' }}>{step.icon}</div>
                </div>
                <h3 className="font-editorial text-2xl font-semibold mb-2">{step.title}</h3>
                <p className="text-sm font-medium mb-4" style={{ color: 'var(--color-terracotta)' }}>{step.subtitle}</p>
                <p className="mb-6" style={{ color: 'var(--color-charcoal-soft)' }}>{step.description}</p>
                <a
                  href={step.cta.href}
                  className="inline-flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
                  style={{ color: 'var(--color-terracotta)' }}
                >
                  {step.cta.label}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// KEY DIFFERENTIATORS (trimmed from 5 to 3)
// ============================================
const DifferentiatorsSection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();

  const differentiators = [
    {
      title: 'The Full Loop in One Place',
      description: 'Issue → Solution → Action Group → Outcome. No more juggling 5 tools. The entire journey from identifying a problem to tracking results lives here.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
    },
    {
      title: 'Skills-to-Issues Matching',
      description: 'Your expertise connected to issues where you can make the biggest difference. Not just volunteering — targeted impact based on what you actually know how to do.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: 'Cross-Boundary Connection',
      description: 'A group in Houston working on youth unemployment discovers a team in Lagos tackling the same issue. Existing tools coordinate within groups. WEattitude connects across them.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <section className="py-24 lg:py-32" style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}>
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: 'var(--color-ochre)' }}>
            Why WEattitude
          </span>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
            What makes this <span className="italic font-normal" style={{ color: 'var(--color-sand)' }}>different</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {differentiators.map((item, index) => (
            <div
              key={item.title}
              className={`p-8 rounded-sm transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', transitionDelay: `${index * 150}ms` }}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-6" style={{ background: 'rgba(255,255,255,0.1)' }}>
                {item.icon}
              </div>
              <h3 className="font-editorial text-xl font-semibold mb-3">{item.title}</h3>
              <p className="text-sm opacity-70 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// SINGLE CTA SECTION
// ============================================
const CTASection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [contactError, setContactError] = useState<string | null>(null);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactStatus('submitting');
    setContactError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id ?? null;

      const { error } = await supabase.from('user_messages').insert({
        name: contactForm.name.trim(),
        email: contactForm.email.trim().toLowerCase(),
        message: contactForm.message.trim(),
        user_id: userId,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
      });

      if (error) {
        throw error;
      }

      setContactStatus('success');
      setContactForm({ name: '', email: '', message: '' });
    } catch (err: any) {
      setContactStatus('error');
      setContactError(
        err?.message || 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <section id="join" className="py-24 lg:py-32 relative overflow-hidden" style={{ background: 'var(--color-terracotta)' }}>
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
      }} />

      <div ref={ref} className="relative max-w-5xl mx-auto px-6 lg:px-12" style={{ color: 'var(--color-warm-white)' }}>
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="font-editorial text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
              Ready to make<br />a difference?
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Join a growing community of people who believe change starts with those closest to the problem.
            </p>
          </div>

          {/* Two columns */}
          <div className="grid lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Left: Get Started */}
            <div className="p-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <h3 className="font-editorial text-xl font-semibold mb-3">Get Started</h3>
              <p className="opacity-80 mb-6 text-sm">
                Create your free account and start making an impact today.
              </p>
              <a
                href="/sign-in"
                className="group inline-flex items-center gap-3 px-6 py-3 rounded-full font-semibold transition-all hover:scale-105 w-full justify-center"
                style={{ background: 'var(--color-warm-white)', color: 'var(--color-terracotta)' }}
              >
                Get Started — It's Free
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="/issues"
                className="inline-flex items-center justify-center gap-2 w-full mt-3 px-6 py-3 rounded-full font-semibold border transition-all hover:scale-105 text-sm"
                style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
              >
                Or browse first
              </a>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-4 mt-6 text-xs opacity-60">
                {['Free forever', 'Open source', 'No gatekeepers'].map((badge) => (
                  <span key={badge} className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Contact */}
            <div id="contact" className="p-8 rounded-lg scroll-mt-24" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <h3 className="font-editorial text-xl font-semibold mb-3">Get in Touch</h3>
              <p className="opacity-80 mb-6 text-sm">
                Questions, ideas, or want to get involved? We'd love to hear from you.
              </p>

              {contactStatus === 'success' ? (
                <div className="flex items-center justify-center h-[200px]">
                  <p className="text-lg font-medium opacity-90 text-center">
                    Thank you! Your message has been received. We'll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    disabled={contactStatus === 'submitting'}
                    className="w-full px-4 py-2.5 rounded-lg text-sm placeholder:text-white/50 disabled:opacity-60"
                    style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    disabled={contactStatus === 'submitting'}
                    className="w-full px-4 py-2.5 rounded-lg text-sm placeholder:text-white/50 disabled:opacity-60"
                    style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                  />
                  <textarea
                    placeholder="Your message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    rows={3}
                    disabled={contactStatus === 'submitting'}
                    className="w-full px-4 py-2.5 rounded-lg text-sm placeholder:text-white/50 resize-none disabled:opacity-60"
                    style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                  />
                  <button
                    type="submit"
                    disabled={contactStatus === 'submitting'}
                    className="w-full px-6 py-2.5 rounded-lg font-semibold text-sm transition-all hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ background: 'var(--color-warm-white)', color: 'var(--color-terracotta)' }}
                  >
                    {contactStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                  {contactStatus === 'error' && (
                    <p
                      className="text-sm text-center px-4 py-2.5 rounded-lg"
                      style={{ background: 'rgba(220,38,38,0.25)', color: 'white' }}
                    >
                      {contactError || 'Something went wrong. Please try again.'}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOOTER (simplified)
// ============================================
const Footer: React.FC = () => {
  return (
    <footer style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <a href="/" className="inline-flex items-baseline mb-4">
              <span className="font-editorial text-2xl font-semibold">WE</span>
              <span className="font-editorial text-2xl font-light italic opacity-70">attitude</span>
              <span className="text-xs opacity-40">.org</span>
            </a>
            <p className="opacity-60 max-w-sm mb-6">
              Built by communities, for communities. Decentralizing philanthropy, one solution at a time.
            </p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-semibold mb-4">Explore</h4>
            <ul className="space-y-3 text-sm opacity-60">
              <li><a href="/issues" className="hover:opacity-100 transition-opacity">Browse Issues</a></li>
              <li><a href="/solutions" className="hover:opacity-100 transition-opacity">Browse Solutions</a></li>
              <li><a href="/action-groups" className="hover:opacity-100 transition-opacity">Action Groups</a></li>
            </ul>
            <h5 className="font-medium mt-6 mb-3 text-sm opacity-70">More</h5>
            <ul className="space-y-3 text-sm opacity-50">
              <li><a href="/motivationals" className="hover:opacity-100 transition-opacity">Motivational Content</a></li>
              <li><a href="/handouts" className="hover:opacity-100 transition-opacity">Handout Sheets</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-3 text-sm opacity-60">
              <li><a href="/privacy" className="hover:opacity-100 transition-opacity">Privacy Policy</a></li>
              <li><a href="/terms" className="hover:opacity-100 transition-opacity">Terms of Service</a></li>
              <li>
                <button
                  type="button"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="hover:opacity-100 transition-opacity text-left"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm opacity-50">
          <p>&copy; 2026 WEattitude.org. All rights reserved.</p>
          <p>Decentralizing philanthropy for a better world.</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// STICKY MOBILE CTA (stabilized)
// ============================================
const StickyMobileCTA: React.FC = () => {
  const [pastHero, setPastHero] = useState(false);
  const [nearBottom, setNearBottom] = useState(false);

  useEffect(() => {
    const heroTrigger = document.getElementById('hero-end-trigger');
    const bottomTrigger = document.getElementById('join');

    if (!heroTrigger) return;

    // Single ref object to track both states and derive visibility
    const showObserver = new IntersectionObserver(
      ([entry]) => setPastHero(!entry.isIntersecting),
      { threshold: 0 }
    );

    const hideObserver = new IntersectionObserver(
      ([entry]) => setNearBottom(entry.isIntersecting),
      { threshold: 0.1 }
    );

    showObserver.observe(heroTrigger);
    if (bottomTrigger) hideObserver.observe(bottomTrigger);

    return () => {
      showObserver.disconnect();
      hideObserver.disconnect();
    };
  }, []);

  // Visible only when scrolled past hero AND not near the bottom CTA
  const isVisible = pastHero && !nearBottom;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
      style={{
        background: 'rgba(250, 247, 242, 0.98)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid var(--color-sand)',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <p
            className="font-editorial text-sm font-semibold truncate"
            style={{ color: 'var(--color-charcoal)' }}
          >
            Ready to take action?
          </p>
          <p
            className="text-xs truncate"
            style={{ color: 'var(--color-charcoal-muted)' }}
          >
            Join for free — no gatekeepers
          </p>
        </div>
        <a
          href="/sign-in"
          className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white active:scale-95"
          style={{
            background: 'var(--color-terracotta)',
            boxShadow: '0 4px 12px -2px rgba(194, 65, 12, 0.4)',
          }}
        >
          Get Started
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </a>
      </div>
      <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
    </div>
  );
};

// ============================================
// MAIN TRIMMED LANDING PAGE
// ============================================
const LandingPageTrimmed: React.FC = () => {
  // Handle hash fragments (e.g. /#contact) — React SPAs don't scroll
  // automatically. Retry briefly because animations / lazy sections may
  // mount the target element a beat after the route renders.
  useEffect(() => {
    const hash = window.location.hash?.replace(/^#/, '');
    if (!hash) return;

    let attempts = 0;
    const maxAttempts = 20;
    const interval = window.setInterval(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.clearInterval(interval);
      } else if (++attempts >= maxAttempts) {
        window.clearInterval(interval);
      }
    }, 100);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="landing-redesign">
      <SharedStyles />
      <Navigation />
      <HeroSection />
      <HowItWorksSection />
      <DifferentiatorsSection />
      <CTASection />
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default LandingPageTrimmed;
